import { createContext, useEffect, useReducer } from "react";
import AuthService from "../services/Auth";
import jwt from "jwt-decode";
import moment from "moment";
import Cookies from "universal-cookie";
import { useSnackbar } from "notistack";

var ActionType;
(function (ActionType) {
  ActionType["INITIALIZE"] = "INITIALIZE";
  ActionType["LOGIN"] = "LOGIN";
  ActionType["LOGOUT"] = "LOGOUT";
  ActionType["VERIFY"] = "VERIFY";
})(ActionType || (ActionType = {}));

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  isExpired: false,
  user: null,
};

const cookies = new Cookies();

const AuthContext = createContext({
  ...initialState,
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
});

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isExpired: false,
      isInitialized: true,
      user,
    };
  },
  LOGIN: (state, action) => {
    const { user } = action.payload;
    return {
      ...state,
      isAuthenticated: true,
      isExpired: false,
      user,
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    isExpired: false,
    user: null,
  }),
  VERIFY: (state, action) => {
    const { user, isAuthenticated, isExpired } = action.payload;
    return {
      ...state,
      user,
      isAuthenticated,
      isExpired,
    };
  },
};

const reducer = (state, action) =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

export const AuthContextProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const initialize = async () => {
      try {
        const accessToken = cookies.get("access_token");
        if (accessToken) {
          // verify token
          const user = await AuthService.verifyToken();
          dispatch({
            type: ActionType.INITIALIZE,
            payload: {
              isAuthenticated: true,
              user,
            },
          });
        } else {
          dispatch({
            type: ActionType.INITIALIZE,
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      } catch (err) {
        enqueueSnackbar("มีบางอย่างผิดพลาด กรุณาลองใหม่ภายหลัง", {
          variant: "error",
        });
        dispatch({
          type: ActionType.INITIALIZE,
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    };
    initialize();
  }, [enqueueSnackbar]);

  const login = async (email, password) => {
    const auth = await AuthService.postLogin({
      email: email.toLowerCase(),
      password,
    });
    const { exp } = await jwt(auth.token.access_token);
    const formatExpireDate = moment.unix(exp).format();

    cookies.set("access_token", auth.token.access_token, {
      expires: new Date(formatExpireDate),
      path: "/",
    });

    const user = auth.employee;

    dispatch({
      type: ActionType.LOGIN,
      payload: {
        user,
      },
    });
  };

  const logout = async () => {
    const token = await cookies.get("access_token");
    if (token) {
      cookies.remove("access_token", { path: "/" });
      dispatch({ type: ActionType.LOGOUT });
    }
  };

  const verify = async () => {
    try {
      const user = await AuthService.verifyToken();
      if (!user) {
        console.log("user not found!");
        dispatch({
          type: ActionType.VERIFY,
          payload: {
            user,
            isAuthenticated: false,
            isExpired: true,
          },
        });
      } else {
        dispatch({
          type: ActionType.VERIFY,
          payload: {
            user,
            isAuthenticated: true,
            isExpired: false,
          },
        });
      }
    } catch (err) {
      console.log("token err!");
      dispatch({
        type: ActionType.VERIFY,
        payload: {
          isAuthenticated: false,
          isExpired: true,
        },
      });
    }
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        verify,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
